import { truncate } from 'lodash';

function truncateString(str: string, length: number, omission: string = '...'): string {
  if (str.length <= length) return str;
  return str.slice(0, length - omission.length) + omission;
}

interface FormattedLog {
  timestamp: string;
  level: string;
  message: string;
  details: Record<string, any>;
}

const MAX_STRING_LENGTH = 300;
const TRUNCATE_SUFFIX = ' [truncated...]';
const TRUNCATE_OBJECT_MESSAGE = '\n[Content truncated. Use isFullLog: true to see complete data]';

export class LogFormatter {
  private static sensitiveKeys = ['pid', 'hostname', '_formatted'];
  private static systemKeys = ['time', 'level', 'msg', 'pid', 'hostname'];
  private static maxObjectKeys = 10;

  // Add support for log categories with visual icons/prefixes
  private static categoryIcons: Record<string, string> = {
    'api': '🌐 ',
    'db': '💾 ',
    'auth': '🔒 ',
    'error': '❌ ',
    'warn': '⚠️ ',
    'stream': '📡 ',
    'request': '📥 ',
    'response': '📤 ',
    'perf': '⚡ ',
    'cache': '📦 ',
    'ai': '🤖 ',
    'tool': '🛠️ '
  };

  // Add separators for critical log sections
  private static separators: Record<string, string> = {
    'error': `\n${'\x1b[31m'}${'='.repeat(80)}${'\x1b[0m'}\n`,
    'critical': `\n${'\x1b[41m\x1b[37m'}${'!'.repeat(80)}${'\x1b[0m'}\n`
  };

  // Format stack traces for better readability
  private static formatStackTrace(stack: string[] | string): string {
    if (!stack) return '';
    
    const lines = Array.isArray(stack) ? stack : stack.split('\n');
    if (lines.length <= 1) return Array.isArray(stack) ? stack[0] : stack;
    
    const colors = {
      reset: "\x1b[0m",
      red: "\x1b[31m",
      gray: "\x1b[90m",
      yellow: "\x1b[33m"
    };

    return lines.map((line, index) => {
      // First line (error message) in red
      if (index === 0) return `${colors.red}${line}${colors.reset}`;
      
      // Add indentation and highlight file paths
      const matches = line.match(/(at\s+)([^(]+)(\(?)([^)]*)(.*)/);
      if (matches) {
        const [_, prefix, fn, openParen, filePath, rest] = matches;
        return `  ${colors.gray}${prefix}${colors.yellow}${fn}${colors.gray}${openParen}${filePath}${rest}${colors.reset}`;
      }
      
      return `  ${colors.gray}${line}${colors.reset}`;
    }).join('\n');
  }

  private static truncateValue(value: any, isFullLog = false, depth = 0): any {
    if (isFullLog) return value;
    if (depth > 2) return '[Nested Object]';

    // Try to parse JSON strings to avoid excessive escaping in logs
    if (typeof value === 'string') {
        // Check if the string looks like JSON and try to parse it
        if ((value.startsWith('{') && value.endsWith('}')) || 
            (value.startsWith('[') && value.endsWith(']'))) {
            try {
                const parsed = JSON.parse(value);
                // Return the parsed object instead of the stringified version
                return this.truncateValue(parsed, isFullLog, depth + 1);
            } catch (e) {
                // Not valid JSON, continue with normal string handling
            }
        }
        
        if (value.length <= MAX_STRING_LENGTH) return value;
        return truncateString(value, MAX_STRING_LENGTH, TRUNCATE_SUFFIX);
    }

    if (Array.isArray(value)) {
      if (value.length > this.maxObjectKeys) {
        return [
          ...value.slice(0, this.maxObjectKeys).map(item => this.truncateValue(item, isFullLog, depth + 1)),
          `[${value.length - this.maxObjectKeys} more items...]`
        ];
      }
      return value.map(item => this.truncateValue(item, isFullLog, depth + 1));
    }

    if (typeof value === 'object' && value !== null) {
      const entries = Object.entries(value);
      if (entries.length > this.maxObjectKeys) {
        const truncatedObj: Record<string, any> = {};
        entries.slice(0, this.maxObjectKeys).forEach(([key, val]) => {
          truncatedObj[key] = this.truncateValue(val, isFullLog, depth + 1);
        });
        truncatedObj['_truncated'] = `[${entries.length - this.maxObjectKeys} more fields...]`;
        return truncatedObj;
      }

      const truncatedObj: Record<string, any> = {};
      for (const [key, val] of entries) {
        truncatedObj[key] = this.truncateValue(val, isFullLog, depth + 1);
      }
      return truncatedObj;
    }

    return value;
  }

  private static cleanObject(obj: Record<string, any>, isFullLog = false): Record<string, any> {
    const cleaned: Record<string, any> = {};
    let isLargeObject = false;

    for (const [key, value] of Object.entries(obj)) {
      if (!isFullLog && this.sensitiveKeys.includes(key)) continue;
      if (!this.systemKeys.includes(key)) {
        cleaned[key] = this.truncateValue(value, isFullLog);
        if (typeof value === 'object' && value !== null && Object.keys(value).length > this.maxObjectKeys) {
          isLargeObject = true;
        }
      }
    }

    if (isLargeObject && !isFullLog) {
      cleaned['_note'] = TRUNCATE_OBJECT_MESSAGE;
    }

    return cleaned;
  }

  static format(logEntry: string | Record<string, any>, isFullLog = false): FormattedLog {
    const log = typeof logEntry === 'string' ? JSON.parse(logEntry) : logEntry;

    const formatted: FormattedLog = {
      timestamp: new Date(log.time).toLocaleTimeString(),
      level: log.level || 'INFO',
      message: log.msg || '',
      details: this.cleanObject(log, isFullLog)
    };

    return formatted;
  }

  // Modify the detailsFormatter to attempt to parse string values that might be stringified JSON
  private static detailsFormatter(obj: Record<string, any>, indent = 0, colors: Record<string, string>): string {
    if (Object.keys(obj).length === 0) return '';
    
    const padding = ' '.repeat(indent);
    let result = '';
    
    for (const [key, value] of Object.entries(obj)) {
        const keyStr = `${colors.cyan}${key}${colors.reset}`;
        
        // Special handling for error stacks
        if (key === 'error' && typeof value === 'object' && value !== null) {
            result += `${padding}${keyStr}: {\n`;
            
            if (value.message) {
                result += `${padding}  ${colors.cyan}message${colors.reset}: ${colors.red}${value.message}${colors.reset}\n`;
            }
            
            if (value.name) {
                result += `${padding}  ${colors.cyan}name${colors.reset}: ${colors.red}${value.name}${colors.reset}\n`;
            }
            
            if (value.stack) {
                result += `${padding}  ${colors.cyan}stack${colors.reset}: \n${padding}    ${this.formatStackTrace(value.stack)}\n`;
            }
            
            result += `${padding}}\n`;
            continue;
        }
        
        // Try to parse JSON strings to avoid excessive escaping
        if (typeof value === 'string' && 
            ((value.startsWith('{') && value.endsWith('}')) || 
             (value.startsWith('[') && value.endsWith(']')))) {
            try {
                const parsed = JSON.parse(value);
                result += `${padding}${keyStr}: `;
                
                if (typeof parsed === 'object' && parsed !== null) {
                    result += '{\n';
                    result += this.detailsFormatter(parsed, indent + 2, colors);
                    result += `${padding}}\n`;
                } else {
                    result += `${parsed}\n`;
                }
                continue;
            } catch (e) {
                // Not valid JSON, continue with normal handling
            }
        }
        
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            // Handle nested objects
            result += `${padding}${keyStr}: {\n`;
            result += this.detailsFormatter(value, indent + 2, colors);
            result += `${padding}}\n`;
        } else if (Array.isArray(value)) {
            // Handle arrays
            result += `${padding}${keyStr}: [\n`;
            value.forEach((item, i) => {
                if (typeof item === 'object' && item !== null) {
                    result += `${padding}  ${colors.dim}[${i}]${colors.reset}: `;
                    result += typeof item === 'object' ? '{\n' + this.detailsFormatter(item, indent + 4, colors) + `${padding}  }\n` : `${item}\n`;
                } else {
                    const itemStr = typeof item === 'string' ? `"${item}"` : item;
                    result += `${padding}  ${colors.dim}[${i}]${colors.reset}: ${itemStr}\n`;
                }
            });
            result += `${padding}]\n`;
        } else {
            // Handle primitive values
            const valueStr = typeof value === 'string' ? `"${value}"` : value;
            result += `${padding}${keyStr}: ${valueStr}\n`;
        }
    }
    
    return result;
  }

  static formatToString(logEntry: string | Record<string, any>, isFullLog = false): string {
    const formatted = this.format(logEntry, isFullLog);
    
    // Define terminal colors using ANSI escape codes
    const colors = {
        reset: "\x1b[0m",
        bright: "\x1b[1m",
        dim: "\x1b[2m",
        red: "\x1b[31m",
        green: "\x1b[32m",
        yellow: "\x1b[33m",
        blue: "\x1b[34m",
        magenta: "\x1b[35m",
        cyan: "\x1b[36m",
        white: "\x1b[37m",
        gray: "\x1b[90m",
        bgRed: "\x1b[41m",
        bgGreen: "\x1b[42m",
        bgYellow: "\x1b[43m",
        bgBlue: "\x1b[44m"
    };
    
    // Color mapping for log levels
    const levelColors: Record<string, string> = {
        'INFO': colors.green,
        'ERROR': colors.red,
        'WARN': colors.yellow,
        'DEBUG': colors.cyan
    };
    
    // Extract category from logEntry if available
    const log = typeof logEntry === 'string' ? JSON.parse(logEntry) : logEntry;
    const category = log.category || this.detectCategory(log.msg || '');
    const categoryPrefix = category && this.categoryIcons[category] ? this.categoryIcons[category] : '';
    
    // Format timestamp
    const timestamp = `${colors.gray}[${formatted.timestamp}]${colors.reset}`;
    
    // Format level with appropriate color
    const levelColor = levelColors[formatted.level] || colors.white;
    const level = `${levelColor}${formatted.level}${colors.reset}`;
    
    // Format message with category
    const message = formatted.message ? `${colors.bright}${categoryPrefix}${formatted.message}${colors.reset}` : '';
    
    // Add separator for error logs if needed
    let separator = '';
    if (formatted.level === 'ERROR') {
        separator = formatted.details.critical ? this.separators.critical : this.separators.error;
    }
    
    // Get formatted details - pass colors to the detailsFormatter
    const details = Object.keys(formatted.details).length > 0 
        ? `\n${this.detailsFormatter(formatted.details, 0, colors)}`
        : '';
    
    return `${separator}${timestamp} ${level}: ${message}${details}${separator ? this.separators.error : ''}`;
  }

  static formatBatch(logs: (string | Record<string, any>)[], isFullLog = false): string {
    return logs
      .map(log => this.formatToString(log, isFullLog))
      .join('\n');
  }

  // Helper to detect category from message
  private static detectCategory(message: string): string | null {
    if (!message) return null;
    
    const patterns = [
      { regex: /\[handleStreamedChat\]|\[streamText\]/, category: 'stream' },
      { regex: /\[api\]|\[endpoint\]|request|response/, category: 'api' },
      { regex: /\[db\]|database|mongo|query/, category: 'db' },
      { regex: /\[auth\]|authentication|token|login|logout/, category: 'auth' },
      { regex: /\[error\]|failed|exception|crash/, category: 'error' },
      { regex: /\[warn\]|warning|deprecated/, category: 'warn' },
      { regex: /\[perf\]|performance|timing|slow/, category: 'perf' },
      { regex: /\[cache\]|caching|cached/, category: 'cache' },
      { regex: /\[ai\]|model|llm|claude|openai|anthropic/, category: 'ai' },
      { regex: /\[tool\]|tool call|tool execution/, category: 'tool' }
    ];
    
    for (const pattern of patterns) {
      if (pattern.regex.test(message)) {
        return pattern.category;
      }
    }
    
    return null;
  }
} 